// @flow
import _ from "lodash";
import { delay } from "redux-saga";
import {
	call,
	put,
	takeLatest,
	takeEvery,
	select,
	cancelled,
} from "redux-saga/effects";
import * as actions from "../../actions/teamsDraft";
import * as leaguesDraftActions from '../../actions/leagueDraftUtils';
import * as selectors from '../../selectors';
import { getPreDraftList } from "../../selectors/team";

const checkErrorsIn = errors => {
	if (_.size(errors)) {
		throw new Error(_.get(_.first(errors), "text", "Unknown error"));
	}
};

export const createSagas = (API: Object): Object => {
	function* updatePreDraftList({ payload }) {
		yield put(actions.clearUpdatePreDraftListSuccessFlag());

		const old_list = yield select(getPreDraftList);

		try {
			yield put(actions.forceUpdatePreDraftList(payload.list));

			const { errors } = yield call(API.teamsDraft.update_list, payload);
			checkErrorsIn(errors);
			yield put(actions.postPreDraftListSuccess(payload.list));
			yield call(delay, 5000);
		}
		catch (error) {
			yield put(actions.postPreDraftListFailed(error.message));
		}
		finally {
			if (yield cancelled()) {
				yield put(actions.forceUpdatePreDraftList(old_list));
			}
		}

		yield put(actions.clearUpdatePreDraftListSuccessFlag());
	}

	function* resetPreDraftList({ payload }) {
		try {
			const { errors } = yield call(API.teamsDraft.reset_list, payload);
			checkErrorsIn(errors);
			yield call(showPreDraftList, {
				payload: { league_id: payload.league_id },
			});
		}
		catch (error) {
			yield put(actions.postResetPreDraftListFailed(error.message));
		}
	}

	function* updatePlayerPreDraftListOrder({
		payload: { player_id, team_id, new_order },
	}) {
		const position = parseInt(new_order, 10) - 1;
		const int_player_id = parseInt(player_id, 10);

		if (!_.isFinite(position) || !_.isFinite(int_player_id)) {
			throw new Error(
				"player_id and new_order fields should be a number"
			);
		}

		const old_list = yield select(getPreDraftList);
		const list = [...old_list];
		const max_pos = list.length;
		const min_pos = 0;
		const position_in_range = Math.min(
			Math.max(position, min_pos),
			max_pos
		);

		list.splice(old_list.indexOf(int_player_id), 1);
		list.splice(position_in_range, 0, int_player_id);

		yield put(actions.postPreDraftList({ team_id, list }));
	}

	function* addPlayerToPreDraftList({ payload: { player_id, team_id } }) {
		const list = yield select(getPreDraftList);

		yield put(
			actions.postPreDraftList({
				team_id,
				list: [...list, parseInt(player_id, 10)],
			})
		);
	}

	function* removePlayerFromPreDraftList({
		payload: { player_id, team_id, silent = false },
	}) {
		const old_list = yield select(getPreDraftList);
		const new_list = _.without(old_list, parseInt(player_id, 10));

		if (silent) {
			yield put(actions.forceUpdatePreDraftList(new_list));
		}
		else {
			yield put(actions.postPreDraftList({ team_id, list: new_list }));
		}
	}

	function* showPreDraftList({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.show_list,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.fetchPreDraftSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchPreDraftFailed(error.message));
		}
	}

	function* pickPlayer({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.pick_player,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.postPickedPlayerSuccess(result));
		}
		catch (error) {
			yield put(actions.postPickedPlayerFailed(error.message));
		}
	}

	function* toggleAutoPick({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.toggle_auto_pick,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.postAutoPickFlagSuccess(result));
		}
		catch (error) {
			yield put(actions.postAutoPickFlagFailed(error.message));
		}
	}

	function* update({ payload }) {
		const { id, lineup, old_lineup } = payload;
		try {
			yield put(
				leaguesDraftActions.forceTeamUpdate({
					lineup,
					id,
				})
			);
			const { result, errors } = yield call(API.teamsDraft.update, {
				lineup,
				id,
			});

			checkErrorsIn(errors);

			if (result) {
				yield put(
					leaguesDraftActions.userTeamUpdated({
						...result,
						lineup,
						id,
					})
				);
				yield put(actions.postTeamUpdateSuccess({ lineup, id }));
			}
		}
		catch (error) {
			yield put(
				leaguesDraftActions.forceTeamUpdate({
					lineup: old_lineup,
					id,
				})
			);
			yield put(actions.postTeamUpdateFailed(error.message));
		}
		finally {
			if (yield cancelled()) {
				yield put(
					leaguesDraftActions.forceTeamUpdate({
						lineup: old_lineup,
						id,
					})
				);
			}
		}
	}

	function* showSnapshot({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.snapshot,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.fetchSnapshotSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchSnapshotFailed(error.message));
		}
	}

	function* getLineups({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.lineups,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.fetchLineupsSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchLineupsFailed(error.message));
		}
	}

	function* getTeam({ payload }) {
		try {
			const { result, errors } = yield call(API.teamsDraft.show, payload);
			checkErrorsIn(errors);
			yield put(actions.fetchTeamSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchTeamFailed(error.message));
		}
	}

	function* getMyTeams({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.show_my,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.fetchMyTeamsSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchMyTeamsFailed(error.message));
		}
	}

	function* postRequestWaiver({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.request_waiver_trade,
				payload
			);
			checkErrorsIn(errors);

			yield put(actions.postRequestWaiverSuccess(result));
		}
		catch (error) {
			yield put(actions.postRequestWaiverFailed(error.message));
		}
	}

	function* fetchMakeTrade({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.make_trade,
				payload
			);
			checkErrorsIn(errors);

			yield put(actions.fetchMakeTradeSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchMakeTradeFailed(error.message));
		}
	}

	function* fetchTrades({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.trades,
				payload
			);
			checkErrorsIn(errors);

			yield put(actions.fetchTradesSuccess(result));
		}
		catch (error) {
			yield put(actions.fetchTradesFailed(error.message));
		}
	}

	function* fetchRejectTrade({ payload: { trade_id, league_id, trades } }) {
		const { my } = trades;
		try {
			const { result, errors } = yield call(API.teamsDraft.reject_trade, {
				trade_id,
			});

			yield put(
				actions.forceTradesSuccess({
					...trades,
					my: my.filter(({ id }) => id !== trade_id),
				})
			);

			checkErrorsIn(errors);

			if (result) {
				yield put(actions.fetchTrades({ league_id }));
				yield put(
					actions.forceTradesSuccess({
						...trades,
					})
				);
			}
		}
		catch (error) {
			yield put(
				actions.forceTradesSuccess({
					...trades,
				})
			);
			yield put(actions.fetchRejectTradeFailed(error.message));
		}
	}

	function* fetchAcceptTrade({ payload: { trade_id, league_id, trades } }) {
		const { my } = trades;
		try {
			yield put(
				actions.forceTradesSuccess({
					...trades,
					my: my.filter(({ id }) => id !== trade_id),
				})
			);

			const { result, errors } = yield call(API.teamsDraft.accept_trade, {
				trade_id,
			});
			checkErrorsIn(errors);

			if (result) {
				yield put(actions.fetchTrades({ league_id }));
			}
			else {
				yield put(
					actions.forceTradesSuccess({
						...trades,
					})
				);
			}
		}
		catch (error) {
			yield put(
				actions.forceTradesSuccess({
					...trades,
				})
			);
			yield put(actions.fetchRejectTradeFailed(error.message));
		}
	}

	function* fetchRemovePlayer({ payload: { team_id, player_id } }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.remove_player,
				{ team_id, player_id }
			);
			checkErrorsIn(errors);

			yield put(actions.fetchRemovePlayerSuccess(result));

			yield put(
				leaguesDraftActions.userTeamUpdatedRemovePlayer({
					lineup: result.lineup,
					id: result.id,
				})
			);
		}
		catch (error) {
			yield put(actions.fetchRemovePlayerFailed(error.message));
		}
	}

	function* fetchBlockTrade({ payload: { trade_id, trades } }) {
		const { proposed } = trades;
		try {
			yield put(
				actions.forceTradesSuccess({
					...trades,
					proposed: proposed.filter(({ id }) => id !== trade_id),
				})
			);
			const { result, errors } = yield call(API.teamsDraft.block_trade, {
				trade_id,
			});
			checkErrorsIn(errors);

			if (!result) {
				yield put(
					actions.forceTradesSuccess({
						...trades,
					})
				);
			}
		}
		catch (error) {
			yield put(
				actions.forceTradesSuccess({
					...trades,
				})
			);
			yield put(actions.fetchBlockTradeFailed(error.message));
		}
	}

	function* fetchApproveTrade({ payload: { trade_id, trades } }) {
		const { proposed } = trades;
		try {
			yield put(
				actions.forceTradesSuccess({
					...trades,
					proposed: proposed.filter(({ id }) => id !== trade_id),
				})
			);
			const { result, errors } = yield call(
				API.teamsDraft.approve_trade,
				{ trade_id }
			);
			checkErrorsIn(errors);

			if (!result) {
				yield put(
					actions.forceTradesSuccess({
						...trades,
					})
				);
			}
		}
		catch (error) {
			yield put(
				actions.forceTradesSuccess({
					...trades,
				})
			);
			yield put(actions.fetchApproveTradeFailed(error.message));
		}
	}
	function* fetchRemoveWaiverTrade({ payload: { id } }) {
		const waivers = yield select(
			selectors.leagueDraft.getWaiverTrades
		);

		try {
			yield put(
				leaguesDraftActions.updatedWaiverRequests(
					waivers.filter(({ id: trade_id }) => trade_id !== id)
				)
			);

			const { result, errors } = yield call(
				API.teamsDraft.remove_waiver_trade,
				{ id }
			);
			checkErrorsIn(errors);

			if (!result) {
				yield put(
					leaguesDraftActions.updatedWaiverRequests(waivers)
				);
			}
		}
		catch (error) {
			yield put(leaguesDraftActions.updatedWaiverRequests(waivers));
			yield put(actions.fetchRemoveWaiverTradeFailed(error.message));
		}
	}

	function* postOrderWaiverTrade({ payload }) {
		const { id, position } = payload;
		const int_trade_id = parseInt(id, 10);

		if (!_.isFinite(position) || !_.isFinite(int_trade_id)) {
			throw new Error("id and new_order fields should be a number");
		}

		const waivers = yield select(
			selectors.leagueDraft.getWaiverTrades
		);
		const list = [...waivers];
		const max_pos = list.length;
		const min_pos = 0;
		const position_in_range = Math.min(
			Math.max(position, min_pos),
			max_pos
		);
		const trade = _.find(waivers, { id: int_trade_id });

		list.splice(_.findIndex(waivers, { id: int_trade_id }), 1);
		list.splice(position_in_range, 0, trade);
		const new_list = list.map((item, i) => {
			item.user_position = i;
			return item;
		});

		try {
			yield put(leaguesDraftActions.updatedWaiverRequests(list));
			const trades = new_list.map(item => {
				return {
					id: item.id,
					position: item.user_position,
				};
			});
			const { result, errors } = yield call(
				API.teamsDraft.manage_order_waiver_trade,
				{ trades }
			);
			checkErrorsIn(errors);
			if (result) {
				yield put(actions.postOrderWaiverTradeSuccess());
			}
			else {
				yield put(actions.postOrderWaiverTradeFailed());
			}
		}
		catch (err) {
			yield put(leaguesDraftActions.updatedWaiverRequests(waivers));
			yield put(actions.postOrderWaiverTradeFailed());
		}
		finally {
			if (yield cancelled()) {
				yield put(
					leaguesDraftActions.updatedWaiverRequests(waivers)
				);
				yield put(actions.postOrderWaiverTradeFailed());
			}
		}
	}

	function* fetchPlayersOnOfferList(action) {
		try {
			const { success, result, errors } = yield call(
				API.teamsDraft.offer_trades,
				{ ...action.payload }
			);
			if (success === 1) {
				yield put(actions.fetchPlayersOnOfferListSuccess(result));
			}
			else {
				yield put(
					actions.fetchPlayersOnOfferListFailed(errors[0].text)
				);
			}
		}
		catch (e) {
			if (process.env.NODE_ENV !== "test") {
				console.error(e);
			}
			yield put(actions.fetchPlayersOnOfferListFailed(e.message));
		}
	}

	function* fetchCancelPlayerOnOffer({ payload: { trade_id, league_id } }) {
		try {
			const { success, result, errors } = yield call(
				API.teamsDraft.reject_trade,
				{ trade_id }
			);
			checkErrorsIn(errors);

			if (success === 1) {
				yield put(actions.fetchCancelPlayerOnOfferSuccess(result));
				yield put(actions.fetchPlayersOnOfferList({ league_id }));
			}
		}
		catch (e) {
			if (process.env.NODE_ENV !== "test") {
				console.error(e);
			}
			yield put(actions.fetchCancelPlayerOnOfferFailed(e.message));
		}
	}

	function* selectKeeper({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.select_keeper,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.postSelectKeeperSuccess(result));
		}
		catch (error) {
			yield put(actions.postSelectKeeperFailed(error.message));
		}
	}

	function* removeKeeper({ payload }) {
		try {
			const { result, errors } = yield call(
				API.teamsDraft.remove_keeper,
				payload
			);
			checkErrorsIn(errors);
			yield put(actions.removeKeeperSuccess(result));
		}
		catch (error) {
			yield put(actions.removeKeeperFailed(error.message));
		}
	}

	function* watch() {
		yield takeLatest(
			actions.updatePlayerPreDraftListOrder,
			updatePlayerPreDraftListOrder
		);
		yield takeLatest(
			actions.removePlayerFromPreDraftList,
			removePlayerFromPreDraftList
		);
		yield takeLatest(
			actions.addPlayerToPreDraftList,
			addPlayerToPreDraftList
		);
		yield takeLatest(actions.postPreDraftList, updatePreDraftList);

		yield takeLatest(actions.fetchPreDraftList, showPreDraftList);
		yield takeLatest(actions.postPickedPlayer, pickPlayer);
		yield takeLatest(actions.postSelectKeeper, selectKeeper);
		yield takeLatest(actions.removeKeeper, removeKeeper);
		yield takeLatest(actions.postAutoPickFlag, toggleAutoPick);

		yield takeLatest(actions.postTeamUpdate, update);

		yield takeLatest(actions.fetchSnapshot, showSnapshot);
		yield takeLatest(actions.fetchLineups, getLineups);
		yield takeEvery(actions.fetchTeam, getTeam);
		yield takeLatest(actions.fetchMyTeams, getMyTeams);
		yield takeLatest(actions.postRequestWaiver, postRequestWaiver);
		yield takeLatest(actions.fetchMakeTrade, fetchMakeTrade);
		yield takeLatest(actions.fetchTrades, fetchTrades);
		yield takeLatest(actions.fetchRejectTrade, fetchRejectTrade);
		yield takeLatest(actions.fetchAcceptTrade, fetchAcceptTrade);
		yield takeLatest(actions.fetchRemovePlayer, fetchRemovePlayer);
		yield takeLatest(actions.fetchBlockTrade, fetchBlockTrade);
		yield takeLatest(actions.fetchApproveTrade, fetchApproveTrade);
		yield takeLatest(
			actions.fetchRemoveWaiverTrade,
			fetchRemoveWaiverTrade
		);
		yield takeLatest(actions.postOrderWaiverTrade, postOrderWaiverTrade);
		yield takeLatest(actions.postResetPreDraftList, resetPreDraftList);
		yield takeLatest(
			actions.fetchPlayersOnOfferList,
			fetchPlayersOnOfferList
		);
		yield takeLatest(
			actions.fetchCancelPlayerOnOffer,
			fetchCancelPlayerOnOffer
		);
	}

	return {
		updatePlayerPreDraftListOrder,
		addPlayerToPreDraftList,
		removePlayerFromPreDraftList,
		updatePreDraftList,
		resetPreDraftList,
		showPreDraftList,
		pickPlayer,
		selectKeeper,
		removeKeeper,
		toggleAutoPick,
		update,
		showSnapshot,
		getLineups,
		getTeam,
		postRequestWaiver,
		fetchRejectTrade,
		fetchAcceptTrade,
		fetchRemovePlayer,
		fetchBlockTrade,
		fetchApproveTrade,
		fetchRemoveWaiverTrade,
		postOrderWaiverTrade,
		fetchPlayersOnOfferList,
		fetchCancelPlayerOnOffer,
		watch,
	};
};

export default createSagas;


// WEBPACK FOOTER //
// ./src/modules/sagas/teamDraft/index.js